import React, {useCallback, useEffect, useState, useRef} from 'react';
import {formatDateTime} from '../utils/dateTime';
import {useNavigate, useParams} from 'react-router-dom';
import createAxiosInstance from "../support/AxiosSupport";
import Spinner from "../components/Spinner";
import AuthImage from "../components/AuthImage";
import AddClothingItemModal from "../components/AddClothingItemModal";
import AddAiRecognitionModal from "../components/AddAiRecognitionModal";
import {AiRecognitionRequest, RECOGNITION_STATUS_LABELS, toEditItem} from "../utils/aiRecognition";
import OneInOneOutDialog from "../components/OneInOneOutDialog";
import {isOneInOneOutEnabled} from "../service/preferencesCache";
import {SparklesIcon, XIcon} from "../components/icons";
import {useToast} from "../context/ToastContext";
import {formatModelCaption} from "../support/modelLabels";
import {useDocumentTitle} from '../hooks/useDocumentTitle';


const STATUS_COLORS: Record<string, string> = {
    NEW: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200',
    PENDING: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-200',
    HOLD: 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-200',
    PROCESSING: 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-200',
    REVIEW: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200',
    DONE: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
    ERROR: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200',
    CANCELED: 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
};


const AiRecognitionRequestsPage: React.FC = () => {
    const navigate = useNavigate();
    const {requestId} = useParams<{requestId?: string}>();
    const [requests, setRequests] = useState<AiRecognitionRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [reviewModalOpen, setReviewModalOpen] = useState(false);
    const [reviewRequest, setReviewRequest] = useState<AiRecognitionRequest | null>(null);
    // «Одно пришло — одно ушло»: созданная из заявки вещь для диалога кандидатов на выбытие
    const [oneInOneOutItem, setOneInOneOutItem] = useState<{id: string; name: string} | null>(null);
    const [promptsModalRequest, setPromptsModalRequest] = useState<AiRecognitionRequest | null>(null);
    const [addModalOpen, setAddModalOpen] = useState(false);
    // Модалка повторного распознавания: заявка + комментарий-уточнение для нового прогона.
    const [retryRequest, setRetryRequest] = useState<AiRecognitionRequest | null>(null);
    const [retryComment, setRetryComment] = useState('');
    const [retrySubmitting, setRetrySubmitting] = useState(false);
    const initialLoadDone = useRef(false);
    const deepLinkHandled = useRef(false);
    const {showToast} = useToast();

    const fetchRequests = useCallback(async (showLoading = true) => {
        if (showLoading) setLoading(true);
        try {
            const api = await createAxiosInstance();
            const res = await api.get<AiRecognitionRequest[]>('/ai-recognition');
            setRequests(res.data);
        } catch (err) {
            console.error('Error fetching AI requests:', err);
            setRequests([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useDocumentTitle();

    useEffect(() => {
        if (!initialLoadDone.current) {
            initialLoadDone.current = true;
            fetchRequests(true);
        }
    }, [fetchRequests]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (!reviewModalOpen && !promptsModalRequest && !addModalOpen && !retryRequest) {
                fetchRequests(false);
            }
        }, 5000);
        return () => clearInterval(interval);
    }, [fetchRequests, reviewModalOpen, promptsModalRequest, addModalOpen, retryRequest]);

    // Deep link: /ai-recognition/:requestId — auto-open review modal for that request
    useEffect(() => {
        if (!requestId || deepLinkHandled.current || loading || requests.length === 0) return;
        const target = requests.find(r => r.id === requestId);
        if (target) {
            deepLinkHandled.current = true;
            if (target.status === 'REVIEW') {
                setReviewRequest(target);
                setReviewModalOpen(true);
            }
            // scroll to item regardless of status
            setTimeout(() => {
                document.getElementById(`ai-request-${requestId}`)?.scrollIntoView({behavior: 'smooth', block: 'center'});
            }, 100);
        }
    }, [requestId, requests, loading]);

    const handleDelete = useCallback(async (id: string) => {
        if (!window.confirm('Удалить запрос на распознавание?')) return;
        try {
            const api = await createAxiosInstance();
            await api.delete(`/ai-recognition/${id}`);
            fetchRequests(false);
        } catch (err) {
            console.error('Error deleting request:', err);
        }
    }, [fetchRequests]);

    // Повтор — через модалку комментария: textarea предзаполняется текущим описанием заявки.
    const openRetry = useCallback((req: AiRecognitionRequest) => {
        setRetryRequest(req);
        setRetryComment(req.userDescription || '');
    }, []);

    const closeRetry = useCallback(() => {
        setRetryRequest(null);
        setRetryComment('');
    }, []);

    const handleRetrySubmit = useCallback(async () => {
        if (!retryRequest) return;
        setRetrySubmitting(true);
        try {
            const api = await createAxiosInstance();
            const comment = retryComment.trim();
            // Пустой комментарий — запрос без тела: описание заявки на сервере не меняется.
            await api.post(`/ai-recognition/${retryRequest.id}/retry`, comment ? {description: comment} : undefined);
            closeRetry();
            fetchRequests(false);
        } catch (err) {
            console.error('Error retrying request:', err);
            showToast('Не удалось повторить распознавание', 'error');
        } finally {
            setRetrySubmitting(false);
        }
    }, [retryRequest, retryComment, closeRetry, fetchRequests, showToast]);

    const handleCancel = useCallback(async (id: string) => {
        if (!window.confirm('Отменить запрос?')) return;
        try {
            const api = await createAxiosInstance();
            await api.post(`/ai-recognition/${id}/cancel`);
            fetchRequests(false);
        } catch (err) {
            console.error('Error canceling request:', err);
        }
    }, [fetchRequests]);

    const openReview = useCallback((req: AiRecognitionRequest) => {
        setReviewRequest(req);
        setReviewModalOpen(true);
    }, []);

    const closeReview = useCallback(() => {
        setReviewModalOpen(false);
        setReviewRequest(null);
    }, []);

    // После сохранения остаёмся на странице заявок (поток «очередь заявок» не прерывается);
    // переход на карточку — по action-ссылке в тосте.
    const handleReviewSuccess = useCallback((createdItem?: { id: string; name?: string }) => {
        // Update-режим (sourceItemId) обновляет существующую вещь — новая вещь не появилась
        const isNewItem = !!createdItem?.id && !reviewRequest?.sourceItemId;
        fetchRequests(false);
        closeReview();
        if (createdItem?.id) {
            const itemId = createdItem.id;
            showToast('Вещь сохранена', 'success', {
                label: 'Перейти к вещи',
                onClick: () => navigate(`/clothing/${itemId}`)
            });
            if (isNewItem) {
                isOneInOneOutEnabled().then(enabled => {
                    if (enabled) {
                        setOneInOneOutItem({id: itemId, name: createdItem.name ?? 'Новая вещь'});
                    }
                });
            }
        }
    }, [fetchRequests, closeReview, navigate, showToast, reviewRequest]);

    const formatDate = (s: string) => formatDateTime(s);

    const canCancel = (status: string) =>
        ['NEW', 'HOLD', 'PENDING'].includes(status);

    // Повтор доступен из ERROR (сбой) и REVIEW (результат не устроил — уточнить комментарием).
    const canRetry = (status: string) => status === 'ERROR' || status === 'REVIEW';

    const canDelete = (status: string) => status !== 'PROCESSING';

    const canReview = (status: string) => status === 'REVIEW';


    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    Запросы на AI-распознавание
                </h1>
                <button
                    onClick={() => setAddModalOpen(true)}
                    className="btn-primary flex items-center gap-2 self-start"
                >
                    <SparklesIcon className="w-5 h-5"/>
                    Распознать по фото
                </button>
            </div>

            {loading ? (
                <div className="flex justify-center py-12">
                    <Spinner/>
                </div>
            ) : requests.length === 0 ? (
                <div className="card p-12 text-center">
                    <p className="text-gray-600 dark:text-gray-400 text-lg">
                        Нет запросов. Добавьте фото на странице вещей.
                    </p>
                </div>
            ) : (
                <div className="card overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                Фото
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                Статус
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                Модель
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                Создан
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                Ошибка
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                Запрос к модели
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                Действия
                            </th>
                        </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                        {requests.map((req) => (
                            <tr key={req.id}
                                id={`ai-request-${req.id}`}
                                onClick={() => setPromptsModalRequest(req)}
                                className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer${req.id === requestId ? ' ring-2 ring-inset ring-brand-500' : ''}`}>
                                <td className="px-4 py-3">
                                    <div className="flex gap-1">
                                        {req.photoKeys.slice(0, 2).map((key) => (
                                            <div key={key} className="w-12 h-12 rounded overflow-hidden bg-gray-100 dark:bg-gray-700">
                                                <AuthImage imageKey={key} alt="" variant="thumbnail" className="w-full h-full object-cover"/>
                                            </div>
                                        ))}
                                    </div>
                                </td>
                                <td className="px-4 py-3">
                                    <span className={`px-2 py-1 rounded text-xs font-medium ${STATUS_COLORS[req.status] || 'bg-gray-100 dark:bg-gray-700'}`}>
                                        {RECOGNITION_STATUS_LABELS[req.status] || req.status}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                    {formatModelCaption(req.model) || '—'}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                    {formatDate(req.createdAt)}
                                </td>
                                <td className="px-4 py-3 text-sm text-red-600 dark:text-red-400 max-w-xs truncate">
                                    {req.errorMessage || '—'}
                                </td>
                                <td className="px-4 py-3">
                                    <button
                                        onClick={() => setPromptsModalRequest(req)}
                                        className="btn-secondary text-sm py-1 px-2"
                                        title="Запрос к модели и описание"
                                    >
                                        Просмотр
                                    </button>
                                </td>
                                <td className="px-4 py-3 text-right space-x-2" onClick={e => e.stopPropagation()}>
                                    {/* В строке REVIEW primary — «Проверить»; «Повторить» вторичен (одна primary на decision region) */}
                                    {canRetry(req.status) && (
                                        <button
                                            onClick={() => openRetry(req)}
                                            className={`${req.status === 'REVIEW' ? 'btn-secondary' : 'btn-primary'} text-sm py-1 px-2`}
                                            data-testid={`retry-button-${req.id}`}
                                        >
                                            Повторить
                                        </button>
                                    )}
                                    {canReview(req.status) && (
                                        <button
                                            onClick={() => openReview(req)}
                                            className="btn-primary text-sm py-1 px-2"
                                        >
                                            Проверить
                                        </button>
                                    )}
                                    {canCancel(req.status) && (
                                        <button
                                            onClick={() => handleCancel(req.id)}
                                            className="btn-danger text-sm py-1 px-2"
                                        >
                                            Отменить
                                        </button>
                                    )}
                                    {canDelete(req.status) && (
                                        <button
                                            onClick={() => handleDelete(req.id)}
                                            className="btn-secondary text-sm py-1 px-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                                            title="Удалить"
                                        >
                                            Удалить
                                        </button>
                                    )}
                                    {req.status === 'DONE' && req.clothingItemId && (
                                        <button
                                            onClick={() => navigate(`/clothing/${req.clothingItemId}`)}
                                            className="btn-secondary text-sm py-1 px-2"
                                        >
                                            К вещи
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}

            <AddAiRecognitionModal
                isOpen={addModalOpen}
                onClose={() => setAddModalOpen(false)}
                onSuccess={() => {
                    setAddModalOpen(false);
                    fetchRequests(false);
                }}
            />

            {reviewRequest && (
                <AddClothingItemModal
                    isOpen={reviewModalOpen}
                    onClose={closeReview}
                    onSuccess={handleReviewSuccess}
                    editItem={toEditItem(reviewRequest)}
                    aiDetectedFlaws={reviewRequest.aiResult?.detectedFlaws ?? null}
                    aiConfirmRequestId={reviewRequest.id}
                    aiConfirmUpdateMode={!!reviewRequest.sourceItemId}
                    onAiConfirmSuccess={(item) => handleReviewSuccess(item)}
                />
            )}

            {oneInOneOutItem && (
                <OneInOneOutDialog
                    addedItemId={oneInOneOutItem.id}
                    addedItemName={oneInOneOutItem.name}
                    onClose={() => setOneInOneOutItem(null)}
                />
            )}

            {retryRequest && (
                <div className="fw-overlay fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
                    <div
                        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md flex flex-col"
                        data-testid="retry-modal"
                    >
                        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-600">
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                                Повторное распознавание
                            </h2>
                            <button
                                onClick={closeRetry}
                                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                                aria-label="Закрыть"
                            >
                                <XIcon className="w-6 h-6"/>
                            </button>
                        </div>
                        <div className="p-4 space-y-4">
                            <div>
                                <label
                                    htmlFor="retry-comment"
                                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                                >
                                    Комментарий для AI (по желанию)
                                </label>
                                <textarea
                                    id="retry-comment"
                                    data-testid="retry-comment"
                                    value={retryComment}
                                    onChange={e => setRetryComment(e.target.value)}
                                    rows={3}
                                    maxLength={2000}
                                    className="input-field w-full"
                                    placeholder="Например: это замшевые ботинки, а не нубук"
                                />
                                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                    Комментарий заменит описание заявки и будет учтён при новом распознавании.
                                </p>
                            </div>
                            <div className="flex justify-end gap-2">
                                <button type="button" onClick={closeRetry} className="btn-secondary">
                                    Отмена
                                </button>
                                <button
                                    type="button"
                                    onClick={handleRetrySubmit}
                                    disabled={retrySubmitting}
                                    className="btn-primary"
                                    data-testid="retry-submit"
                                >
                                    {retrySubmitting ? 'Отправка...' : 'Повторить'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {promptsModalRequest && (
                <div className="fw-overlay fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-600">
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                                Детали запроса
                            </h2>
                            <button
                                onClick={() => setPromptsModalRequest(null)}
                                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                                <XIcon className="w-6 h-6"/>
                            </button>
                        </div>
                        <div className="p-4 overflow-y-auto space-y-4 flex-1">
                            {promptsModalRequest.photoKeys?.length > 0 && (
                                <div className="flex flex-wrap gap-2" data-testid="detail-photos">
                                    {promptsModalRequest.photoKeys.map((key) => (
                                        <div key={key} className="w-24 h-24 rounded overflow-hidden bg-gray-100 dark:bg-gray-700">
                                            <AuthImage imageKey={key} alt="" className="w-full h-full object-cover"/>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                    <span className="text-gray-500 dark:text-gray-400">Статус: </span>
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[promptsModalRequest.status] || 'bg-gray-100 dark:bg-gray-700'}`}>
                                        {RECOGNITION_STATUS_LABELS[promptsModalRequest.status] || promptsModalRequest.status}
                                    </span>
                                </div>
                                <div><span className="text-gray-500 dark:text-gray-400">Модель: </span>{formatModelCaption(promptsModalRequest.model) || '—'}</div>
                                <div><span className="text-gray-500 dark:text-gray-400">Создан: </span>{formatDate(promptsModalRequest.createdAt)}</div>
                                <div><span className="text-gray-500 dark:text-gray-400">Обновлён: </span>{formatDate(promptsModalRequest.updatedAt ?? '')}</div>
                            </div>
                            {promptsModalRequest.errorMessage && (
                                <div>
                                    <h3 className="text-sm font-medium text-red-600 dark:text-red-400 mb-1">Ошибка</h3>
                                    <pre className="p-3 rounded bg-red-50 dark:bg-red-900/20 text-sm whitespace-pre-wrap break-words">
                                        {promptsModalRequest.errorMessage}
                                    </pre>
                                    {/* CTHS-25: при ошибке распознавания — деградация в ручной ввод: подсказываем повтор/ручное добавление. */}
                                    {promptsModalRequest.status === 'ERROR' && (
                                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                            Можно повторить распознавание или добавить вещь вручную.
                                        </p>
                                    )}
                                </div>
                            )}
                            {promptsModalRequest.userDescription && (
                                <div>
                                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Описание пользователя</h3>
                                    <pre className="p-3 rounded bg-gray-100 dark:bg-gray-700 text-sm whitespace-pre-wrap break-words">
                                        {promptsModalRequest.userDescription}
                                    </pre>
                                </div>
                            )}
                            {promptsModalRequest.systemPrompt && (
                                <div>
                                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Запрос к модели: правила</h3>
                                    <pre className="p-3 rounded bg-gray-100 dark:bg-gray-700 text-sm whitespace-pre-wrap break-words max-h-64 overflow-y-auto">
                                        {promptsModalRequest.systemPrompt}
                                    </pre>
                                </div>
                            )}
                            {promptsModalRequest.userPrompt && (
                                <div>
                                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Запрос к модели: данные вещи</h3>
                                    <pre className="p-3 rounded bg-gray-100 dark:bg-gray-700 text-sm whitespace-pre-wrap break-words">
                                        {promptsModalRequest.userPrompt}
                                    </pre>
                                </div>
                            )}
                            {!promptsModalRequest.userDescription && !promptsModalRequest.systemPrompt && !promptsModalRequest.userPrompt && (
                                <p className="text-gray-500 dark:text-gray-400">Запрос к модели ещё не сохранён (обработка не начата)</p>
                            )}
                            {promptsModalRequest.aiResult && (
                                <div>
                                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ответ модели</h3>
                                    <pre className="p-3 rounded bg-gray-100 dark:bg-gray-700 text-sm whitespace-pre-wrap break-words max-h-64 overflow-y-auto" data-testid="detail-airesult">
                                        {JSON.stringify(promptsModalRequest.aiResult, null, 2)}
                                    </pre>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AiRecognitionRequestsPage;
